import { useEffect, useId, useRef, useState } from 'react'
import { Link, useLocation, useSearchParams } from 'react-router-dom'
import { IoInformationCircle } from 'react-icons/io5'
import { RiArrowDownSLine } from 'react-icons/ri'
import axios from 'axios'
import { createPortal } from 'react-dom'
import Seo from './Seo'
import ChampionIconWithFallback from '../shared/ChampionIcon'
import { assetUrl, axiosForAPI } from '../helper_functions/utilities'
import { PLATFORM_OPTIONS } from '../helper_functions/searchRouting'
import { scopedPlatformOptions, useDeploymentCapabilities } from '../context/DeploymentCapabilitiesContext'

const ROLE_OPTIONS = [
    { value: '', label: 'ALL ROLES' },
    { value: 'TOP', label: 'TOP' },
    { value: 'JUNGLE', label: 'JUNGLE' },
    { value: 'MIDDLE', label: 'MID' },
    { value: 'BOTTOM', label: 'BOTTOM' },
    { value: 'UTILITY', label: 'SUPPORT' },
]

const TIER_OPTIONS = [
    { value: 'Emerald+', label: 'EMERALD+', icon: `${process.env.PUBLIC_URL}/assets/ranked_icons/Rank=Emerald.png` },
    { value: 'Diamond+', label: 'DIAMOND+', icon: `${process.env.PUBLIC_URL}/assets/ranked_icons/Rank=Diamond.png` },
    { value: 'Master+', label: 'MASTER+', icon: `${process.env.PUBLIC_URL}/assets/ranked_icons/Rank=Master.png` },
    { value: 'Grandmaster+', label: 'GRANDMASTER+', icon: `${process.env.PUBLIC_URL}/assets/ranked_icons/Rank=Grandmaster.png` },
    { value: 'Iron', label: 'IRON', icon: `${process.env.PUBLIC_URL}/assets/ranked_icons/Rank=Iron.png` },
    { value: 'Bronze', label: 'BRONZE', icon: `${process.env.PUBLIC_URL}/assets/ranked_icons/Rank=Bronze.png` },
    { value: 'Silver', label: 'SILVER', icon: `${process.env.PUBLIC_URL}/assets/ranked_icons/Rank=Silver.png` },
    { value: 'Gold', label: 'GOLD', icon: `${process.env.PUBLIC_URL}/assets/ranked_icons/Rank=Gold.png` },
    { value: 'Platinum', label: 'PLATINUM', icon: `${process.env.PUBLIC_URL}/assets/ranked_icons/Rank=Platinum.png` },
    { value: 'Emerald', label: 'EMERALD', icon: `${process.env.PUBLIC_URL}/assets/ranked_icons/Rank=Emerald.png` },
    { value: 'Diamond', label: 'DIAMOND', icon: `${process.env.PUBLIC_URL}/assets/ranked_icons/Rank=Diamond.png` },
    { value: 'Master', label: 'MASTER', icon: `${process.env.PUBLIC_URL}/assets/ranked_icons/Rank=Master.png` },
    { value: 'Grandmaster', label: 'GRANDMASTER', icon: `${process.env.PUBLIC_URL}/assets/ranked_icons/Rank=Grandmaster.png` },
    { value: 'Challenger', label: 'CHALLENGER', icon: `${process.env.PUBLIC_URL}/assets/ranked_icons/Rank=Challenger.png` },
]

const MASTERY_OPTIONS = [
    { value: '', label: 'ALL MASTERY' },
    { value: '0-4', label: 'LOW' },
    { value: '5-8', label: 'MID-LOW' },
    { value: '9-13', label: 'MID' },
    { value: '14-21', label: 'MID-HIGH' },
    { value: '22+', label: 'HIGH' },
]

const TIER_ORDER = ['S', 'A', 'B', 'C', 'D']
const SORT_OPTIONS = [
    { value: 'tier', label: 'TIER' },
    { value: 'win_rate', label: 'WIN RATE' },
    { value: 'games', label: 'GAMES' },
    { value: 'ban_rate', label: 'BAN RATE' },
]
const TIER_STYLES = {
    S: 'border-cyan-700/60 bg-cyan-950/25 text-cyan-200',
    A: 'border-sky-800/60 bg-sky-950/20 text-sky-200',
    B: 'border-zinc-700 bg-zinc-900/55 text-zinc-200',
    C: 'border-amber-800/50 bg-amber-950/15 text-amber-200',
    D: 'border-orange-900/60 bg-orange-950/15 text-orange-200',
}
const ROLE_LABELS = { TOP: 'Top', JUNGLE: 'Jungle', MIDDLE: 'Mid', BOTTOM: 'Bottom', UTILITY: 'Support' }
const ROLE_ICONS = {
    TOP: 'top',
    JUNGLE: 'jungle',
    MIDDLE: 'middle',
    BOTTOM: 'bottom',
    UTILITY: 'utility',
}

function percent(value) {
    return value === null || value === undefined ? '-' : `${Number(value).toFixed(1)}%`
}

function winRateColor(value) {
    const numericValue = Number(value)
    if (!Number.isFinite(numericValue)) return 'rgb(161 161 170)'
    const distance = Math.min(1, Math.abs(numericValue - 50) / 8)
    const target = numericValue >= 50 ? [103, 232, 249] : [253, 230, 138]
    const neutral = [148, 163, 184]
    const color = neutral.map((channel, index) => Math.round(channel + ((target[index] - channel) * distance)))
    return `rgb(${color.join(' ')})`
}

function banRateColor(value) {
    const numericValue = Number(value)
    if (!Number.isFinite(numericValue)) return 'rgb(161 161 170)'
    if (numericValue < 5) return 'rgb(103 232 249)'
    const distance = Math.min(1, Math.max(0, (numericValue - 5) / 25))
    const neutral = [148, 163, 184]
    const amber = [253, 230, 138]
    const color = neutral.map((channel, index) => Math.round(channel + ((amber[index] - channel) * distance)))
    return `rgb(${color.join(' ')})`
}

function winRateDeltaColor(value) {
    const numericValue = Number(value)
    if (!Number.isFinite(numericValue)) return 'rgb(161 161 170)'
    const intensity = Math.min(1, Math.abs(numericValue) / 5)
    const target = numericValue >= 0 ? [103, 232, 249] : [253, 230, 138]
    const neutral = [148, 163, 184]
    const color = neutral.map((channel, index) => Math.round(channel + ((target[index] - channel) * intensity)))
    return `rgb(${color.join(' ')})`
}

function signedPercent(value) {
    if (value === null || value === undefined) return '-'
    const numericValue = Number(value)
    if (!Number.isFinite(numericValue)) return '-'
    return `${numericValue > 0 ? '+' : ''}${numericValue.toFixed(1)}%`
}

function FilterSelect({ label, icon, options, value, onChange, disabled = false, highlight = false }) {
    return (
        <label className="min-w-0">
            <span className={`mb-1 block text-[10px] font-bold uppercase tracking-[0.16em] ${highlight ? 'text-cyan-300' : 'text-zinc-500'}`}>{label}</span>
            <div className="relative">
                {icon ? <img alt="" aria-hidden="true" className="pointer-events-none absolute left-2.5 top-1/2 z-10 h-5 w-5 -translate-y-1/2 object-contain" src={icon} /> : null}
                <select
                    className={`h-9 w-full appearance-none rounded-md border py-2 pr-8 text-[11px] font-semibold uppercase tracking-[0.12em] transition-colors focus:outline-none disabled:cursor-not-allowed disabled:border-zinc-800 disabled:bg-zinc-950 disabled:text-zinc-600 disabled:opacity-70 ${icon ? 'pl-9' : 'px-3'} ${highlight ? 'border-cyan-700/80 bg-cyan-950/35 text-cyan-100 shadow-[inset_0_1px_0_rgba(103,232,249,0.08),0_0_0_1px_rgba(34,211,238,0.08)] focus:border-cyan-400/80 focus:ring-1 focus:ring-cyan-400/45' : 'border-zinc-700 bg-zinc-900 text-zinc-200 focus:border-cyan-700/70 focus:ring-1 focus:ring-cyan-700/45'}`}
                    disabled={disabled}
                    value={value}
                    onChange={onChange}
                >
                    {options.map((option) => <option key={option.value || 'all'} value={option.value} disabled={option.disabled}>{option.label}</option>)}
                </select>
                <RiArrowDownSLine aria-hidden="true" className={`pointer-events-none absolute right-2.5 top-1/2 z-10 h-4 w-4 -translate-y-1/2 ${highlight ? 'text-cyan-300' : 'text-zinc-500'}`} />
            </div>
        </label>
    )
}

function MetricInfoHeader({ label, description, active = false, descending = true, onClick }) {
    const infoButtonRef = useRef(null)
    const tooltipId = useId()
    const [tooltipVisible, setTooltipVisible] = useState(false)
    const [tooltipPosition, setTooltipPosition] = useState(null)

    useEffect(() => {
        if (!tooltipVisible) return undefined

        function updateTooltipPosition() {
            const button = infoButtonRef.current
            if (!button) return
            const rect = button.getBoundingClientRect()
            const tooltipWidth = 224
            const maxLeft = Math.max(8, window.innerWidth - tooltipWidth - 8)
            const left = Math.min(Math.max(8, rect.right - tooltipWidth), maxLeft)
            const above = rect.top >= 120
            setTooltipPosition({ above, left, top: above ? rect.top - 8 : rect.bottom + 8 })
        }

        updateTooltipPosition()
        window.addEventListener('resize', updateTooltipPosition)
        window.addEventListener('scroll', updateTooltipPosition, true)
        return () => {
            window.removeEventListener('resize', updateTooltipPosition)
            window.removeEventListener('scroll', updateTooltipPosition, true)
        }
    }, [tooltipVisible])

    return (
        <div className="flex items-center justify-end gap-1 text-right">
            {onClick ? (
                <button
                    aria-pressed={active}
                    className={`whitespace-nowrap text-[10px] font-bold uppercase tracking-[0.14em] transition ${active ? 'text-cyan-100' : 'text-zinc-500 hover:text-zinc-300'}`}
                    type="button"
                    onClick={onClick}
                >
                    {label}{active ? <span className="ml-1 text-xs font-black text-cyan-300">{descending ? '↓' : '↑'}</span> : null}
                </button>
            ) : <span className="whitespace-nowrap text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-500">{label}</span>}
            <span className="inline-flex shrink-0">
                <button
                    aria-label={`${label} information`}
                    aria-describedby={tooltipVisible ? tooltipId : undefined}
                    className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-full text-zinc-500 transition-colors hover:text-cyan-300 focus:outline-none focus-visible:text-cyan-300"
                    ref={infoButtonRef}
                    type="button"
                    onBlur={() => setTooltipVisible(false)}
                    onFocus={() => setTooltipVisible(true)}
                    onMouseEnter={() => setTooltipVisible(true)}
                    onMouseLeave={() => setTooltipVisible(false)}
                >
                    <IoInformationCircle aria-hidden="true" className="h-3.5 w-3.5" />
                </button>
            </span>
            {tooltipVisible && tooltipPosition && typeof document !== 'undefined' ? createPortal(
                <span
                    id={tooltipId}
                    className="pointer-events-none fixed z-[2000] w-56 rounded-md border border-zinc-700 bg-zinc-950 px-2.5 py-2 text-left text-[10px] font-medium normal-case leading-snug tracking-normal text-zinc-300 shadow-xl"
                    role="tooltip"
                    style={{
                        left: tooltipPosition.left,
                        top: tooltipPosition.top,
                        transform: tooltipPosition.above ? 'translateY(-100%)' : undefined,
                    }}
                >
                    {description}
                </span>,
                document.body,
            ) : null}
        </div>
    )
}

function SortButton({ option, active, align = 'left', compact = false, descending = true, onClick }) {
    const alignmentClass = align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left'
    const activeClass = compact ? 'border-cyan-600/80 text-cyan-100' : 'text-cyan-100'
    const inactiveClass = compact ? 'border-zinc-800 text-zinc-500 hover:text-zinc-300' : 'text-zinc-500 hover:text-zinc-300'
    return (
        <button
            aria-pressed={active}
            className={`${compact ? 'w-auto rounded-md border px-2.5 py-1.5' : 'w-full'} whitespace-nowrap ${alignmentClass} text-[10px] font-bold uppercase tracking-[0.14em] transition ${active ? activeClass : inactiveClass}`}
            type="button"
            onClick={onClick}
        >
            {option.label}{active ? <span className="ml-1 text-xs font-black text-cyan-300">{descending ? '↓' : '↑'}</span> : null}
        </button>
    )
}

function TierRow({ result, rank, isTopRank, showTierBaseline }) {
    return (
        <div className={`grid min-w-[760px] ${showTierBaseline ? 'grid-cols-[44px_minmax(220px,1.6fr)_44px_76px_100px_100px_100px_92px_92px_100px]' : 'grid-cols-[44px_minmax(220px,1.6fr)_44px_76px_100px_92px_92px_100px]'} items-center gap-3 border-t border-zinc-800/75 px-3 py-2.5 first:border-t-0 ${isTopRank ? 'bg-cyan-900/25 shadow-[inset_0_1px_0_rgba(103,232,249,0.3),inset_0_-1px_0_rgba(34,211,238,0.18)]' : 'hover:bg-cyan-950/10'}`}>
            <div className={`text-sm tabular-nums ${isTopRank ? 'text-cyan-100' : 'text-zinc-500'}`}>{rank}</div>
            <Link className="group flex min-w-0 items-center gap-3" to={`/analytics/champions/${encodeURIComponent(result.name_id)}`}>
                <span className="h-9 w-9 shrink-0 overflow-hidden rounded-full border border-zinc-700 bg-zinc-900">
                    <ChampionIconWithFallback alt={`${result.name} icon`} className="h-full w-full scale-[1.18] object-cover" src={assetUrl(`/champion_icons/${result.name_id}.png`)} />
                </span>
                <span className="min-w-0">
                    <span className="block truncate text-sm font-semibold text-zinc-100 group-hover:text-cyan-100">{result.name}</span>
                    <span className="mt-0.5 block text-[10px] font-semibold uppercase tracking-[0.12em] text-zinc-500">{ROLE_LABELS[result.role_id] || result.role_id}</span>
                </span>
            </Link>
            <div className="flex justify-center">
                {ROLE_ICONS[result.role_id] ? <img alt={`${ROLE_LABELS[result.role_id] || result.role_id} role`} className="h-5 w-5 object-contain" src={`${process.env.PUBLIC_URL}/assets/role_icons/position-${ROLE_ICONS[result.role_id]}-light.svg`} /> : null}
            </div>
            <div><span className={`inline-flex h-7 w-9 items-center justify-center rounded-md border text-xs font-bold ${TIER_STYLES[result.tier]}`}>{result.tier}</span></div>
            <div className="text-right text-sm font-semibold tabular-nums" style={{ color: winRateColor(result.win_rate) }}>{percent(result.win_rate)}</div>
            {showTierBaseline ? <>
                <div className="text-right text-sm font-semibold tabular-nums" style={{ color: winRateColor(result.tier_win_rate) }}>{percent(result.tier_win_rate)}</div>
                <div className="text-right text-sm font-semibold tabular-nums" style={{ color: winRateDeltaColor(result.win_rate_delta) }}>{signedPercent(result.win_rate_delta)}</div>
            </> : null}
            <div className="text-right text-sm tabular-nums text-zinc-300">{result.games.toLocaleString()}</div>
            <div className="text-right text-sm tabular-nums text-zinc-400">{percent(result.role_pick_rate)}</div>
            <div className="text-right text-sm tabular-nums" style={{ color: banRateColor(result.ban_rate) }}>{percent(result.ban_rate)}</div>
        </div>
    )
}

function MobileTierRow({ result, rank, isTopRank, showTierBaseline }) {
    return (
        <Link className={`grid ${showTierBaseline ? 'grid-cols-[18px_82px_20px_40px_42px_40px_40px_40px]' : 'grid-cols-[18px_96px_20px_42px_48px_42px_42px]'} items-center justify-between gap-x-0 border-t border-zinc-800/75 px-2 py-2 first:border-t-0 ${isTopRank ? 'bg-cyan-900/25 shadow-[inset_0_1px_0_rgba(103,232,249,0.3),inset_0_-1px_0_rgba(34,211,238,0.18)]' : 'hover:bg-cyan-950/10'}`} to={`/analytics/champions/${encodeURIComponent(result.name_id)}`}>
            <div className="text-[11px] tabular-nums text-zinc-500">{rank}</div>
            <div className="flex min-w-0 items-center gap-1.5">
                <span className="h-8 w-8 shrink-0 overflow-hidden rounded-full border border-zinc-700 bg-zinc-900">
                    <ChampionIconWithFallback alt={`${result.name} icon`} className="h-full w-full scale-[1.18] object-cover" src={assetUrl(`/champion_icons/${result.name_id}.png`)} />
                </span>
                <span className="min-w-0 truncate text-xs font-semibold text-zinc-100">{result.name}</span>
            </div>
            <div className="flex justify-center" title={ROLE_LABELS[result.role_id] || result.role_id}>
                {ROLE_ICONS[result.role_id] ? <img alt={`${ROLE_LABELS[result.role_id] || result.role_id} role`} className="h-5 w-5 object-contain" src={`${process.env.PUBLIC_URL}/assets/role_icons/position-${ROLE_ICONS[result.role_id]}-light.svg`} /> : null}
            </div>
            <div className="min-w-0 text-center"><div className="text-[9px] uppercase tracking-[0.08em] text-zinc-600">WR</div><div className="text-[11px] font-semibold tabular-nums" style={{ color: winRateColor(result.win_rate) }}>{percent(result.win_rate)}</div></div>
            {showTierBaseline ? <>
                <div className="min-w-0 text-center"><div className="text-[9px] uppercase tracking-[0.08em] text-zinc-600">DELTA</div><div className="text-[11px] font-semibold tabular-nums" style={{ color: winRateDeltaColor(result.win_rate_delta) }}>{signedPercent(result.win_rate_delta)}</div></div>
            </> : null}
            <div className="min-w-0 text-center"><div className="text-[9px] uppercase tracking-[0.08em] text-zinc-600">GAMES</div><div className="text-[11px] tabular-nums text-zinc-300">{result.games.toLocaleString()}</div></div>
            <div className="min-w-0 text-center"><div className="text-[9px] uppercase tracking-[0.08em] text-zinc-600">ROLE</div><div className="text-[11px] tabular-nums text-zinc-400">{percent(result.role_pick_rate)}</div></div>
            <div className="min-w-0 text-center"><div className="text-[9px] uppercase tracking-[0.08em] text-zinc-600">BAN</div><div className="text-[11px] tabular-nums" style={{ color: banRateColor(result.ban_rate) }}>{percent(result.ban_rate)}</div></div>
        </Link>
    )
}

function MobileTierHeader({ tier }) {
    return (
        <div className="flex items-center gap-2 border-t border-zinc-800/80 bg-zinc-900/75 px-3 py-2 first:border-t-0">
            <span className={`inline-flex h-6 w-8 items-center justify-center rounded-md border text-[11px] font-bold ${TIER_STYLES[tier]}`}>{tier}</span>
            <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-zinc-400">Tier</span>
        </div>
    )
}

export default function ChampionTierList() {
    const deploymentCapabilities = useDeploymentCapabilities()
    const scopedOptions = scopedPlatformOptions(PLATFORM_OPTIONS, deploymentCapabilities)
    const location = useLocation()
    const [searchParams, setSearchParams] = useSearchParams()
    const isHighMasteryReport = location.pathname === '/analytics/tier-list/high-mastery'
    const [platform, setPlatform] = useState(() => searchParams.get('region') || '')
    const [sliceMode, setSliceMode] = useState(() => (searchParams.get('mastery') || isHighMasteryReport ? 'mastery' : 'tier'))
    const [tier, setTier] = useState(() => (searchParams.get('mastery') || isHighMasteryReport ? '' : (searchParams.get('tier') || 'Emerald+')))
    const [roleId, setRoleId] = useState(() => searchParams.get('role') || '')
    const [masteryBucket, setMasteryBucket] = useState(() => searchParams.get('mastery') || (isHighMasteryReport ? '22+' : ''))
    const [patch, setPatch] = useState(() => searchParams.get('patch') || '')
    const [patchOptions, setPatchOptions] = useState([])
    const [patchOptionsLoaded, setPatchOptionsLoaded] = useState(false)
    useEffect(() => {
        if (!isHighMasteryReport) return
        setSliceMode('mastery')
        setTier('')
        setMasteryBucket('22+')
    }, [isHighMasteryReport])
    const [results, setResults] = useState([])
    const [banRateScopeTier, setBanRateScopeTier] = useState('Emerald+')
    const [sortBy, setSortBy] = useState('tier')
    const [sortDescending, setSortDescending] = useState(false)

    const isDescendingSort = sortBy === 'tier' ? !sortDescending : sortDescending
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const year = new Date().getUTCFullYear() % 100
    const tierOptions = TIER_OPTIONS
    const masteryOptions = MASTERY_OPTIONS.slice(1)
    const reportingTypeOptions = [
        { value: 'tier', label: 'TIER' },
        { value: 'mastery', label: 'MASTERY' },
    ]
    const showTierBaseline = sliceMode === 'tier'
    const tierBaselineWinRate = results.find((result) => result.tier_win_rate !== null && result.tier_win_rate !== undefined)?.tier_win_rate

    function handleSliceModeChange(event) {
        const nextMode = event.target.value
        setSliceMode(nextMode)
        if (nextMode === 'tier') {
            setMasteryBucket('')
            setTier(tier || 'Emerald+')
        } else {
            setTier('')
            setMasteryBucket(masteryBucket || masteryOptions[masteryOptions.length - 1].value)
        }
    }

    function selectSort(nextSort) {
        if (nextSort === sortBy) {
            setSortDescending((current) => !current)
            return
        }
        setSortBy(nextSort)
        setSortDescending(nextSort !== 'tier')
    }

    function resetReport() {
        setPlatform('')
        setSliceMode('tier')
        setTier('Emerald+')
        setRoleId('')
        setMasteryBucket('')
        setPatch(patchOptions[0]?.value || '')
        setSortBy('tier')
        setSortDescending(false)
    }

    const sortedResults = [...results].sort((left, right) => {
        let comparison = 0
        if (sortBy === 'tier') comparison = TIER_ORDER.indexOf(left.tier) - TIER_ORDER.indexOf(right.tier)
        else comparison = Number(left[sortBy] || 0) - Number(right[sortBy] || 0)
        if (comparison === 0) comparison = Number(right.quality_edge || 0) - Number(left.quality_edge || 0)
        if (comparison === 0) comparison = Number(right.games || 0) - Number(left.games || 0)
        if (comparison === 0) comparison = String(left.name).localeCompare(String(right.name))
        return sortDescending ? -comparison : comparison
    })
    const mobileTierGroups = sortBy === 'tier'
        ? [...new Set(sortedResults.map((result) => result.tier))].map((tier) => ({
            results: sortedResults.filter((result) => result.tier === tier),
            tier,
        }))
        : []

    useEffect(() => {
        const controller = new AbortController()
        async function loadPatchOptions() {
            try {
                const response = await axiosForAPI.get('/analytics/patch-options/', {
                    params: { year },
                    signal: controller.signal,
                })
                const options = (response.data?.patches || []).map((option) => ({
                    ...option,
                    label: `PATCH ${option.value}`,
                }))
                setPatchOptions(options)
                setPatch((current) => options.some((option) => option.value === current) ? current : (options[0]?.value || ''))
            } catch (requestError) {
                if (!axios.isCancel(requestError) && requestError?.code !== 'ERR_CANCELED') setPatchOptions([])
            } finally {
                if (!controller.signal.aborted) setPatchOptionsLoaded(true)
            }
        }
        loadPatchOptions()
        return () => controller.abort()
    }, [year])

    useEffect(() => {
        const next = new URLSearchParams()
        if (platform) next.set('region', platform)
        if (tier) next.set('tier', tier)
        if (roleId) next.set('role', roleId)
        if (masteryBucket) next.set('mastery', masteryBucket)
        if (patch) next.set('patch', patch)
        setSearchParams(next, { replace: true })
    }, [masteryBucket, patch, platform, roleId, setSearchParams, tier])

    useEffect(() => {
        const controller = new AbortController()
        async function loadTierList() {
            if (!patchOptionsLoaded) return
            if (!patch) {
                setError('Select a patch to load this report.')
                setResults([])
                setLoading(false)
                return
            }
            try {
                setLoading(true)
                setError('')
                const response = await axiosForAPI.get('/analytics/champion-tier-list/', {
                    params: {
                        year,
                        platform: platform || undefined,
                        tier: tier || undefined,
                        patch,
                        masteryBucket: masteryBucket || undefined,
                        roleId: roleId || undefined,
                    },
                    signal: controller.signal,
                })
                setResults(response.data?.results || [])
                setBanRateScopeTier(response.data?.filters?.ban_rate_scope?.tier || 'Emerald+')
            } catch (requestError) {
                if (axios.isCancel(requestError) || requestError?.code === 'ERR_CANCELED') return
                setError(requestError?.response?.data?.detail || 'Unable to load the champion tier list.')
                setResults([])
            } finally {
                if (!controller.signal.aborted) setLoading(false)
            }
        }
        loadTierList()
        return () => controller.abort()
    }, [masteryBucket, patch, patchOptionsLoaded, platform, roleId, tier, year])

    return (
        <div className="mx-auto w-full max-w-screen-lg text-zinc-200">
            <div className="mx-4 pt-6 pb-5 custom-lg:mx-0 sm:pt-8 sm:pb-7">
                <Seo
                    title={`${isHighMasteryReport ? 'High Mastery ' : ''}Champion Tier List | WINRATES.GG™`}
                    description={`${isHighMasteryReport ? 'High mastery ' : ''}role-based League of Legends champion tier rankings using observed performance and sample strength.`}
                    canonical={isHighMasteryReport ? '/analytics/tier-list/high-mastery' : '/analytics/tier-list'}
                />
                <div className="mb-5">
                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-cyan-400">Meta Analysis</div>
                    <h1 className="mt-1 text-2xl font-semibold tracking-tight text-zinc-100 sm:text-3xl">Champion Tier List</h1>
                    <p className="mt-2 max-w-2xl text-sm leading-6 text-zinc-400">Champion Tier Lists: by Region, Tier, Role, Patch, and Mastery Level.</p>
                </div>

                <section className="mb-5 grid gap-3 rounded-xl border border-zinc-800 bg-zinc-950 p-3 sm:grid-cols-6 sm:p-4">
                    <FilterSelect label="REPORTING TYPE" options={reportingTypeOptions} value={sliceMode} onChange={handleSliceModeChange} highlight />
                    {sliceMode === 'tier' ? (
                        <FilterSelect label="Tier" icon={tierOptions.find((option) => option.value === tier)?.icon} options={tierOptions} value={tier} onChange={(event) => {
                            setSliceMode('tier')
                            setTier(event.target.value)
                            setMasteryBucket('')
                        }} />
                    ) : (
                        <FilterSelect label="Mastery" options={masteryOptions} value={masteryBucket} onChange={(event) => {
                            setSliceMode('mastery')
                            setMasteryBucket(event.target.value)
                            setTier('')
                        }} />
                    )}
                    <FilterSelect label="Region" options={scopedOptions} value={platform} onChange={(event) => setPlatform(event.target.value)} />
                    <FilterSelect label="Role" options={ROLE_OPTIONS} value={roleId} onChange={(event) => setRoleId(event.target.value)} />
                    <FilterSelect label="Patch" options={patchOptions.length ? patchOptions : [{ value: '', label: 'CURRENT PATCH' }]} value={patch} onChange={(event) => setPatch(event.target.value)} />
                    <button className="self-end rounded-md border border-zinc-700 px-3 py-2 text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-400 transition hover:border-cyan-700/70 hover:text-cyan-200" type="button" onClick={resetReport}>Reset</button>
                </section>

                {error ? <div className="rounded-lg border border-rose-800/60 bg-rose-950/30 px-4 py-3 text-sm text-rose-100">{error}</div> : null}
                {sliceMode === 'mastery' ? <div className="mb-3 rounded-lg border border-zinc-800/80 bg-zinc-900/45 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-zinc-500">Ban rate remains scoped to {banRateScopeTier} tiers because Riot does not identify the player who issued each ban.</div> : null}
                {loading ? <div className="rounded-xl border border-zinc-800 bg-zinc-950 px-4 py-8 text-sm text-zinc-500">Loading tier list...</div> : null}
                {!loading && !error && !results.length ? <div className="rounded-xl border border-zinc-800 bg-zinc-950 px-4 py-8 text-sm text-zinc-500">No champion-role observations meet the evidence threshold.</div> : null}

                {!loading && !error && results.length ? (
                    <>
                        <div className="mb-3 flex flex-wrap items-center gap-1.5 sm:hidden">
                            {SORT_OPTIONS.map((option) => <SortButton key={option.value} option={option} compact active={sortBy === option.value} descending={isDescendingSort} onClick={() => selectSort(option.value)} />)}
                            {showTierBaseline ? (
                                <div className="ml-auto flex items-center gap-1.5 rounded-md border border-cyan-900/60 bg-cyan-950/25 px-2.5 py-1.5 text-[9px] font-bold uppercase tracking-[0.1em] text-cyan-200">
                                    <span className="text-cyan-400/80">TIER WR</span>
                                    <span className="text-[11px] tabular-nums text-cyan-100">{percent(tierBaselineWinRate)}</span>
                                </div>
                            ) : null}
                        </div>
                        <div className="overflow-hidden rounded-xl border border-zinc-800 bg-zinc-950 sm:hidden">
                            {sortBy === 'tier' ? mobileTierGroups.map((group) => (
                                <div key={group.tier}>
                                    <MobileTierHeader tier={group.tier} />
                                    {group.results.map((result) => {
                                        const rank = sortedResults.indexOf(result) + 1
                                        return <MobileTierRow key={`${result.champion_id}-${result.role_id}`} result={result} rank={rank} isTopRank={rank === 1} showTierBaseline={showTierBaseline} />
                                    })}
                                </div>
                            )) : sortedResults.map((result, index) => <MobileTierRow key={`${result.champion_id}-${result.role_id}`} result={result} rank={index + 1} isTopRank={index === 0} showTierBaseline={showTierBaseline} />)}
                        </div>
                        <div className="hidden overflow-hidden rounded-xl border border-zinc-800 bg-zinc-950 sm:block">
                        <div className="overflow-x-auto">
                            <div className={showTierBaseline ? 'min-w-[900px]' : 'min-w-[760px]'}>
                                <div className={`grid ${showTierBaseline ? 'grid-cols-[44px_minmax(220px,1.6fr)_44px_76px_100px_100px_100px_92px_92px_100px]' : 'grid-cols-[44px_minmax(220px,1.6fr)_44px_76px_100px_92px_92px_100px]'} items-center gap-3 bg-zinc-900/65 px-3 py-3`}>
                                    <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-500">#</div>
                                    <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-500">Champion</div>
                                    <div className="text-center text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-500">Role</div>
                                    <SortButton option={SORT_OPTIONS.find((option) => option.value === 'tier')} align="left" active={sortBy === 'tier'} descending={isDescendingSort} onClick={() => selectSort('tier')} />
                                    <MetricInfoHeader
                                        active={sortBy === 'win_rate'}
                                        descending={isDescendingSort}
                                        description="The win rate of a champion when picked in the relevant skill band (i.e. How Lux performs in Gold) or when piloted by a player in the selected mastery band (i.e. How high mastery Riven players perform)."
                                        label="WIN RATE"
                                        onClick={() => selectSort('win_rate')}
                                    />
                                    {showTierBaseline ? <>
                                        <MetricInfoHeader description="The average win rate of players in the selected tier(s). While the player base has a 50% win rate, fluid skill bands do not." label="TIER WR" />
                                        <MetricInfoHeader description="The win rate of a champion minus the win rate of the average player in the same skill band. This is an estimate for over or under performance." label="WR DELTA" />
                                    </> : null}
                                    <SortButton option={SORT_OPTIONS.find((option) => option.value === 'games')} align="right" active={sortBy === 'games'} descending={isDescendingSort} onClick={() => selectSort('games')} />
                                    <div className="text-right text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-500">ROLE RATE</div>
                                    <SortButton option={SORT_OPTIONS.find((option) => option.value === 'ban_rate')} align="right" active={sortBy === 'ban_rate'} descending={isDescendingSort} onClick={() => selectSort('ban_rate')} />
                                </div>
                                {sortedResults.map((result, index) => <TierRow key={`${result.champion_id}-${result.role_id}`} result={result} rank={index + 1} isTopRank={index === 0} showTierBaseline={showTierBaseline} />)}
                            </div>
                        </div>
                        </div>
                    </>
                ) : null}
            </div>
        </div>
    )
}
